import React, { useState, useEffect, useRef } from "react";
import { Question } from "../types";
import ProgressBar from "./ProgressBar";
import TimerBar from "./TimerBar";
import { CheckIcon, CrossIcon, LightbulbIcon } from "./icons";
import { Sounds } from "../hooks/useSounds";

interface GameScreenProps {
  question: Question;
  totalQuestions: number;
  currentQuestionIndex: number;
  streak: number;
  onAnswer: (points: number, isCorrect: boolean) => void;
  sounds: Sounds;
}

const TOTAL_TIME = 20000; // 20 seconds in ms
const MAX_POINTS = 1000;
const STREAK_BONUS = 100;

// Helper function to handle italics within text segments
const formatItalics = (text: string) => {
  if (!text) return text;
  // Split by italics (*...*)
  const parts = text.split(/(\*[\s\S]+?\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return (
        <em key={i} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

// Helper function for formatting text with bold (**text**) and italics (*text*)
const formatText = (text: string) => {
  if (!text) return null;

  // Split by bold (**...**)
  const parts = text.split(/(\*\*[\s\S]+?\*\*)/g);

  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const content = part.slice(2, -2);
      return (
        <strong key={i} className="font-bold">
          {formatItalics(content)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{formatItalics(part)}</React.Fragment>;
  });
};

const GameScreen: React.FC<GameScreenProps> = ({
  question,
  totalQuestions,
  currentQuestionIndex,
  streak,
  onAnswer,
  sounds,
}) => {
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [pointsAwarded, setPointsAwarded] = useState(0);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const timerRef = useRef<number | null>(null);
  const tickCounterRef = useRef(0);

  const isRound1 = currentQuestionIndex < 7;
  const roundNumber = isRound1 ? 1 : 2;
  const questionsPerRound = 7;
  const questionNumberInRound = isRound1
    ? currentQuestionIndex + 1
    : currentQuestionIndex - 7 + 1;

  useEffect(() => {
    const startDelay = setTimeout(() => {
      timerRef.current = window.setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 100) {
            return 0;
          }

          const newTime = prevTime - 100;
          tickCounterRef.current += 1;

          // Play click sound every 500ms (5 * 100ms interval)
          if (tickCounterRef.current >= 5) {
            const isLoud = newTime < 10000;
            sounds.playClick(isLoud);
            tickCounterRef.current = 0;
          }

          return newTime;
        });
      }, 100);
    }, 1000); // 1-second delay

    return () => {
      clearTimeout(startDelay);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [sounds]);

  useEffect(() => {
    if (timeLeft <= 0 && !showFeedback) {
      if (timerRef.current) clearInterval(timerRef.current);
      sounds.playIncorrect();
      setShowFeedback(true);
      setSelectedAnswer(null); // Timeout means no answer was selected
      setPointsAwarded(0);
      setShowContinueButton(true);
    }
  }, [timeLeft, showFeedback, sounds]);

  const handleAnswerClick = (index: number) => {
    sounds.initAudio(); // Ensure context is running on mobile interaction
    if (showFeedback) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setShowFeedback(true);
    setSelectedAnswer(index);

    const isCorrect = index === question.correctAnswerIndex;
    let points = 0;

    if (isCorrect) {
      const basePoints = Math.max(
        10,
        Math.floor(MAX_POINTS * (timeLeft / TOTAL_TIME))
      );
      // Add streak bonus (current streak is from previous questions, so if they get this right, streak increases for next, but we can award current streak bonus)
      // Let's award bonus based on *resulting* streak.
      const bonus = streak + 1 > 1 ? (streak + 1) * 20 : 0;
      points = basePoints + bonus;

      setPointsAwarded(points);
      sounds.playCorrect();

      // Check if this is the last question of the game
      if (currentQuestionIndex === totalQuestions - 1) {
        // If last question, force a "Continue" button press.
        // This ensures the user interacts with the page to transition to the End Screen,
        // allowing the browser to permit audio playback on the next screen.
        setShowContinueButton(true);
      } else {
        setTimeout(() => {
          onAnswer(points, true);
        }, 2500);
      }
    } else {
      setPointsAwarded(0);
      sounds.playIncorrect();
      setShowContinueButton(true);
    }
  };

  const handleContinue = () => {
    onAnswer(pointsAwarded, pointsAwarded > 0);
  };

  const getButtonClass = (index: number) => {
    if (!showFeedback) {
      // Changed: restricted hover to 'md' breakpoint and up to prevent sticky hover on mobile
      return "bg-white md:hover:bg-teal-100 active:bg-teal-100 text-teal-800";
    }
    if (index === question.correctAnswerIndex) {
      return "bg-green-500 text-white";
    }
    if (index === selectedAnswer && index !== question.correctAnswerIndex) {
      return "bg-red-500 text-white";
    }
    return "bg-white text-teal-800 opacity-60";
  };

  return (
    <div className="animate-fade-in w-full max-w-3xl mx-auto bg-white p-6 md:p-8 rounded-2xl shadow-2xl border-2 border-gray-200 relative">
      {/* Streak Badge */}
      <div className="absolute -top-3 -right-3 md:-right-5">
        {streak > 1 && (
          <div className="bg-orange-500 text-white font-bold py-2 px-4 rounded-full shadow-lg border-4 border-white transform rotate-12 animate-pulse flex items-center gap-1">
            <span>🔥</span>
            <span>Streak: {streak}</span>
          </div>
        )}
      </div>

      <div className="mb-2">
        <div className="flex justify-between items-end mb-1">
          <p className="text-sm font-semibold text-teal-600">
            Round {roundNumber} - Question {questionNumberInRound} of{" "}
            {questionsPerRound}
          </p>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
            Score:{" "}
            <span className="text-teal-600 text-sm">
              {pointsAwarded > 0 && showFeedback ? `+${pointsAwarded}` : ""}
            </span>
          </p>
        </div>
        <ProgressBar
          current={questionNumberInRound}
          total={questionsPerRound}
        />
      </div>

      <TimerBar remainingTime={timeLeft} totalTime={TOTAL_TIME} />

      <div className="mb-6">
        <span
          className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-2 ${
            question.difficulty === "Easy"
              ? "bg-green-100 text-green-800"
              : question.difficulty === "Medium"
              ? "bg-yellow-100 text-yellow-800"
              : question.difficulty === "Hard"
              ? "bg-red-100 text-red-800"
              : "bg-purple-100 text-purple-800"
          }`}
        >
          {question.difficulty}
        </span>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
          {formatText(question.text)}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswerClick(index)}
            disabled={showFeedback}
            className={`flex items-center justify-between p-4 rounded-lg text-left font-semibold transition-all duration-300 transform shadow-md ${getButtonClass(
              index
            )} disabled:cursor-not-allowed`}
          >
            <span className="flex-1 mr-4">{formatText(option)}</span>
            {showFeedback && index === question.correctAnswerIndex && (
              <CheckIcon />
            )}
            {showFeedback &&
              index === selectedAnswer &&
              index !== question.correctAnswerIndex && <CrossIcon />}
          </button>
        ))}
      </div>

      {showFeedback && (
        <div className="mt-6 p-3 bg-gray-100 rounded-lg text-center animate-fade-in">
          {pointsAwarded > 0 ? (
            <div>
              <p className="text-xl font-bold text-green-600">Correct!</p>
              {streak > 0 && (
                <p className="text-sm text-orange-500 font-semibold">
                  Streak Bonus Applied!
                </p>
              )}
            </div>
          ) : selectedAnswer !== null ? (
            <p className="text-xl font-bold text-red-600">Incorrect!</p>
          ) : (
            <p className="text-xl font-bold text-red-600">Time's up!</p>
          )}
        </div>
      )}

      {showFeedback && pointsAwarded === 0 && (
        <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg animate-fade-in">
          <div className="flex items-start">
            <LightbulbIcon />
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-red-800">
                Explanation
              </h3>
              <p className="mt-1 text-red-700">
                {formatText(question.explanation)}
              </p>
            </div>
          </div>
        </div>
      )}

      {showContinueButton && (
        <div className="mt-6 text-center animate-fade-in">
          <button
            onClick={handleContinue}
            className="bg-teal-500 text-white font-bold py-2 px-6 rounded-lg text-lg hover:bg-teal-600 transition-transform transform hover:scale-105 shadow-md"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default GameScreen;
